/* Sportedix — global behaviour (vanilla JS, custom elements, no dependencies) */
(() => {
  const SX = (window.Sportedix = window.Sportedix || {});
  const strings = () => SX.strings || {};
  const routes = () => SX.routes || {};
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- utils */
  SX.fetchConfig = (type = 'json') => ({
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}`, 'X-Requested-With': 'XMLHttpRequest' }
  });

  SX.debounce = (fn, wait = 300) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn.apply(this, args), wait); };
  };

  SX.formatMoney = (cents, format = SX.moneyFormat || '${{amount}}') => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const fmt = (n, precision = 2, thousands = ',', decimal = '.') => {
      if (isNaN(n) || n == null) return '0';
      const num = (n / 100.0).toFixed(precision);
      const parts = num.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return dollars + (parts[1] ? decimal + parts[1] : '');
    };
    const match = format.match(placeholder);
    if (!match) return format;
    let value = '';
    switch (match[1]) {
      case 'amount': value = fmt(cents, 2); break;
      case 'amount_no_decimals': value = fmt(cents, 0); break;
      case 'amount_with_comma_separator': value = fmt(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = fmt(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = fmt(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': value = fmt(cents, 0, ' ', ''); break;
      case 'amount_with_space_separator': value = fmt(cents, 2, ' ', ','); break;
      case 'amount_with_period_and_space_separator': value = fmt(cents, 2, ' ', '.'); break;
      default: value = fmt(cents, 2);
    }
    return format.replace(placeholder, value);
  };

  SX.toast = (message, ms = 4000) => {
    const el = document.querySelector('[data-toast]');
    if (!el || !message) return;
    el.textContent = message;
    el.hidden = false;
    clearTimeout(el._t);
    el._t = setTimeout(() => { el.hidden = true; }, ms);
  };

  /* Focus trap */
  const FOCUSABLE = 'a[href], area[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), iframe, summary';
  SX.focusable = (container) => Array.from(container.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null || el === document.activeElement);
  SX.trapFocus = (container, elementToFocus) => {
    SX.removeTrapFocus();
    SX._trap = (e) => {
      if (e.key !== 'Tab') return;
      const els = SX.focusable(container);
      if (!els.length) { e.preventDefault(); return; }
      const first = els[0];
      const last = els[els.length - 1];
      if (e.shiftKey && (document.activeElement === first || !container.contains(document.activeElement))) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && (document.activeElement === last || !container.contains(document.activeElement))) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', SX._trap);
    const target = elementToFocus || SX.focusable(container)[0] || container;
    setTimeout(() => target && target.focus({ preventScroll: true }), 30);
  };
  SX.removeTrapFocus = (elementToFocus) => {
    if (SX._trap) document.removeEventListener('keydown', SX._trap);
    SX._trap = null;
    if (elementToFocus && document.contains(elementToFocus)) elementToFocus.focus({ preventScroll: true });
  };

  /* Tiny pub/sub */
  const subscribers = {};
  SX.subscribe = (name, cb) => { (subscribers[name] = subscribers[name] || []).push(cb); return () => { subscribers[name] = subscribers[name].filter((c) => c !== cb); }; };
  SX.publish = (name, data) => (subscribers[name] || []).forEach((cb) => cb(data));

  /* ------------------------------------------------------------- cart I/O */
  SX.cartSectionIds = () => {
    const ids = new Set();
    document.querySelectorAll('[data-cart-inner][data-section-id]').forEach((el) => ids.add(el.dataset.sectionId));
    return Array.from(ids);
  };

  SX.renderCartSections = (sections) => {
    if (!sections) return;
    Object.keys(sections).forEach((id) => {
      const html = sections[id];
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector(`[data-cart-inner][data-section-id="${id}"]`) || doc.querySelector('[data-cart-inner]');
      if (!fresh) return;
      document.querySelectorAll(`[data-cart-inner][data-section-id="${id}"]`).forEach((target) => {
        target.innerHTML = fresh.innerHTML;
        Array.from(fresh.attributes).forEach((a) => target.setAttribute(a.name, a.value));
      });
      const count = fresh.getAttribute('data-item-count');
      if (count !== null) SX.updateCartCount(parseInt(count, 10));
    });
  };

  SX.updateCartCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = count > 0 ? (count > 99 ? '99+' : count) : ''; });
    document.querySelectorAll('[data-cart-button]').forEach((btn) => {
      const tpl = btn.dataset.labelTemplate;
      if (tpl) btn.setAttribute('aria-label', tpl.replace('[count]', count));
    });
  };

  SX.addToCart = async (items, sourceForm) => {
    const sections = SX.cartSectionIds();
    const body = { items, sections: sections.join(','), sections_url: window.location.pathname };
    const res = await fetch(`${routes().cart_add}.js`, { ...SX.fetchConfig(), body: JSON.stringify(body) });
    const data = await res.json();
    if (!res.ok || data.status) {
      const err = new Error(data.description || data.message || strings().cartError);
      err.data = data;
      throw err;
    }
    SX.renderCartSections(data.sections);
    SX.publish('cart:updated', { source: sourceForm, items: data.items || [data] });
    return data;
  };

  SX.changeCart = async (line, quantity) => {
    const sections = SX.cartSectionIds();
    const body = { line, quantity, sections: sections.join(','), sections_url: window.location.pathname };
    const res = await fetch(`${routes().cart_change}.js`, { ...SX.fetchConfig(), body: JSON.stringify(body) });
    const data = await res.json();
    if (!res.ok || data.status) {
      const err = new Error(data.description || data.message || strings().cartError);
      throw err;
    }
    SX.renderCartSections(data.sections);
    SX.updateCartCount(data.item_count);
    SX.publish('cart:updated', { cart: data });
    return data;
  };

  SX.openCart = (trigger) => {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && SX.cartType !== 'page') { drawer.open(trigger); return true; }
    window.location.href = routes().cart;
    return false;
  };

  /* ---------------------------------------------- theme editor event bridge */
  const callUp = (start, method, payload) => {
    let el = start;
    while (el && el !== document) {
      if (typeof el[method] === 'function') { el[method](payload); return true; }
      el = el.parentElement;
    }
    return false;
  };
  document.addEventListener('shopify:block:select', (e) => {
    const block = e.target;
    if (block.tagName === 'DETAILS') block.open = true;
    const inner = block.querySelector && block.querySelector(':scope > details, details[data-block-details]');
    if (inner) inner.open = true;
    callUp(block.parentElement, 'onBlockSelect', block);
  });
  document.addEventListener('shopify:block:deselect', (e) => callUp(e.target.parentElement, 'onBlockDeselect', e.target));
  document.addEventListener('shopify:section:select', (e) => {
    const root = e.target;
    const comp = root.querySelector('[data-editor-select]');
    if (comp && typeof comp.onSectionSelect === 'function') comp.onSectionSelect();
  });
  document.addEventListener('shopify:section:deselect', (e) => {
    const comp = e.target.querySelector('[data-editor-select]');
    if (comp && typeof comp.onSectionDeselect === 'function') comp.onSectionDeselect();
  });
  document.addEventListener('shopify:section:load', (e) => {
    initReveal(e.target);
    if (SX.onSectionLoad) SX.onSectionLoad(e.target);
  });
  document.addEventListener('shopify:section:unload', () => { document.body.classList.remove('sx-lock'); SX.removeTrapFocus(); });

  /* ------------------------------------------------------------- drawers */
  class SxDrawer extends HTMLElement {
    connectedCallback() {
      this.root = this.querySelector('.sx-drawer-root') || this;
      this.panel = this.querySelector('.sx-drawer');
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]')) { e.preventDefault(); this.close(); }
      });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
    }
    get isOpen() { return this.root.classList.contains('is-open'); }
    open(trigger) {
      if (this.isOpen) return;
      this.trigger = trigger || document.activeElement;
      this.root.classList.add('is-open');
      this.root.removeAttribute('aria-hidden');
      if (this.panel) this.panel.removeAttribute('inert');
      document.body.classList.add('sx-lock');
      if (this.trigger && this.trigger.setAttribute) this.trigger.setAttribute('aria-expanded', 'true');
      SX.trapFocus(this.panel || this, this.panel && this.panel.querySelector('[data-drawer-initial-focus]'));
    }
    close() {
      if (!this.isOpen) return;
      this.root.classList.remove('is-open');
      this.root.setAttribute('aria-hidden', 'true');
      if (this.panel) this.panel.setAttribute('inert', '');
      document.body.classList.remove('sx-lock');
      if (this.trigger && this.trigger.setAttribute) this.trigger.setAttribute('aria-expanded', 'false');
      SX.removeTrapFocus(this.trigger);
    }
    onSectionSelect() { this.open(); }
    onSectionDeselect() { this.close(); }
  }
  customElements.define('sx-drawer', SxDrawer);

  class MenuDrawer extends SxDrawer {
    connectedCallback() {
      super.connectedCallback();
      const id = this.id;
      this._opener = (e) => {
        const btn = e.target.closest(`[aria-controls="${id}"]`);
        if (btn) { e.preventDefault(); this.open(btn); }
      };
      document.addEventListener('click', this._opener);
      this._mq = window.matchMedia('(min-width: 990px)');
      this._onMq = () => { if (this._mq.matches) this.close(); };
      this._mq.addEventListener('change', this._onMq);
    }
    disconnectedCallback() {
      document.removeEventListener('click', this._opener);
      if (this._mq) this._mq.removeEventListener('change', this._onMq);
      document.body.classList.remove('sx-lock');
    }
    onSectionSelect() {}
    onSectionDeselect() {}
  }
  customElements.define('menu-drawer', MenuDrawer);

  class CartDrawer extends SxDrawer {
    connectedCallback() {
      super.connectedCallback();
      this._opener = (e) => {
        const btn = e.target.closest('[data-cart-button]');
        if (btn && SX.cartType !== 'page') { e.preventDefault(); this.open(btn); }
      };
      document.addEventListener('click', this._opener);
    }
    disconnectedCallback() { document.removeEventListener('click', this._opener); document.body.classList.remove('sx-lock'); }
  }
  customElements.define('cart-drawer', CartDrawer);

  /* ----------------------------------------------------------- cart items */
  class CartItems extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        const remove = e.target.closest('[data-line-remove]');
        if (remove) { e.preventDefault(); this.update(parseInt(remove.dataset.line, 10), 0); }
      });
      this.addEventListener('change', (e) => {
        const input = e.target.closest('[data-line-qty]');
        if (input) this.update(parseInt(input.dataset.line, 10), parseInt(input.value, 10) || 0, input);
      });
      const note = this.querySelector('[data-cart-note]');
      if (note) {
        note.addEventListener('input', SX.debounce(() => {
          fetch(`${routes().cart_update}.js`, { ...SX.fetchConfig(), body: JSON.stringify({ note: note.value }) });
        }, 500));
      }
    }
    async update(line, quantity, input) {
      const row = this.querySelector(`[data-line-row="${line}"]`);
      if (row) row.classList.add('is-loading');
      const focusLine = input ? line : null;
      try {
        const cart = await SX.changeCart(line, quantity);
        const lineItem = cart.items[line - 1];
        if (input && lineItem && lineItem.quantity < quantity) SX.toast(strings().quantityError);
        if (focusLine) {
          const again = document.querySelector(`[data-line-qty][data-line="${focusLine}"]`);
          if (again) again.focus();
        } else {
          const host = this.closest('.sx-drawer') || this;
          const next = host.querySelector('[data-line-qty]') || host.querySelector('a, button');
          if (next) next.focus();
        }
      } catch (err) {
        if (row) row.classList.remove('is-loading');
        SX.toast(err.message);
      }
    }
  }
  customElements.define('cart-items', CartItems);

  /* ------------------------------------------------- cart shipping (live) */
  /* Asks Shopify for real rates to the visitor's country (POST prepare, then poll async). Shows the cheapest
     with its name. If Shopify needs a ZIP/province, errors or is slow, the Liquid estimate stays. Never blocks. */
  class CartShipping extends HTMLElement {
    connectedCallback() {
      if (this.dataset.live !== 'true' || this.dataset.ships !== 'true' || !this.dataset.country) return;
      const cached = SX.shipCache && SX.shipCache[this.dataset.key];
      if (cached) { this.apply(cached); return; }
      this.load();
    }
    async load() {
      const root = (routes().root || '/').replace(/\/$/, '');
      const qs = 'shipping_address%5Bcountry%5D=' + encodeURIComponent(this.dataset.country);
      const key = this.dataset.key;
      try {
        const prep = await fetch(`${root}/cart/prepare_shipping_rates.json?${qs}`, { method: 'POST', headers: { Accept: 'application/json' } });
        if (!prep.ok) return;
        for (let i = 0; i < 6; i += 1) {
          await new Promise((r) => setTimeout(r, i === 0 ? 400 : 900));
          const res = await fetch(`${root}/cart/async_shipping_rates.json?${qs}`, { headers: { Accept: 'application/json' } });
          if (res.status === 202) continue;
          if (!res.ok) return;
          const data = await res.json();
          if (!data) continue;
          const rates = (data.shipping_rates || []).filter((r) => r && r.price !== undefined);
          if (!rates.length) return;
          rates.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
          const best = { name: rates[0].presentment_name || rates[0].name || '', cents: Math.round(parseFloat(rates[0].price) * 100) };
          SX.shipCache = SX.shipCache || {};
          SX.shipCache[key] = best;
          if (this.isConnected && this.dataset.key === key) this.apply(best);
          return;
        }
      } catch (e) { /* keep the estimate */ }
    }
    apply(best) {
      const value = this.querySelector('[data-ship-value]');
      const total = this.querySelector('[data-ship-total]');
      if (!value) return;
      const price = best.cents === 0 ? (strings().shipFree || 'FREE') : SX.formatMoney(best.cents);
      value.textContent = best.name ? (strings().shipRate || '[name]: [price]').replace('[name]', best.name).replace('[price]', price) : price;
      if (total) total.textContent = SX.formatMoney((parseInt(this.dataset.subtotal, 10) || 0) + best.cents, SX.moneyWithCurrencyFormat || SX.moneyFormat);
    }
  }
  customElements.define('cart-shipping', CartShipping);

  /* ------------------------------------------------------ quantity input */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('button[data-step]');
        if (!btn || !this.input) return;
        e.preventDefault();
        const step = parseInt(this.input.step, 10) || 1;
        const min = parseInt(this.input.min, 10) || 0;
        const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
        const prev = parseInt(this.input.value, 10) || min;
        let next = btn.dataset.step === 'up' ? prev + step : prev - step;
        next = Math.max(min, Math.min(max, next));
        if (next === prev) return;
        this.input.value = next;
        this.sync();
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      this.input && this.input.addEventListener('input', () => this.sync());
      this.sync();
    }
    sync() {
      if (!this.input) return;
      const v = parseInt(this.input.value, 10) || 0;
      const min = parseInt(this.input.min, 10) || 0;
      const down = this.querySelector('[data-step="down"]');
      if (down && !this.hasAttribute('data-allow-zero')) down.disabled = v <= Math.max(min, 1);
      const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
      const up = this.querySelector('[data-step="up"]');
      if (up) up.disabled = v >= max;
      const out = this.querySelector('output');
      if (out) out.textContent = v;
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* -------------------------------------------------------- product form */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.form.querySelector('[name="id"]') && (this.form.querySelector('[name="id"]').disabled = false);
      this.submitBtn = this.form.querySelector('[type="submit"]');
      this.errorEl = this.querySelector('[data-form-error]');
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }
    setLoading(on) {
      if (!this.submitBtn) return;
      this.submitBtn.classList.toggle('is-loading', on);
      this.submitBtn.setAttribute('aria-busy', on ? 'true' : 'false');
      const spinner = this.submitBtn.querySelector('.sx-spinner');
      if (spinner) spinner.hidden = !on;
      const label = this.submitBtn.querySelector('[data-label]');
      if (label) {
        if (on) { this._label = label.textContent; label.textContent = strings().adding; }
        else if (this._label) label.textContent = this._label;
      }
    }
    async onSubmit(e) {
      if (SX.cartType === 'page' && !this.hasAttribute('data-always-ajax')) return; // normal POST → cart page
      e.preventDefault();
      if (this.submitBtn && this.submitBtn.getAttribute('aria-disabled') === 'true') return;
      if (this.errorEl) this.errorEl.hidden = true;
      this.setLoading(true);
      const fd = new FormData(this.form);
      const item = { id: parseInt(fd.get('id'), 10), quantity: parseInt(fd.get('quantity') || '1', 10) };
      const props = {};
      for (const [k, v] of fd.entries()) {
        const m = k.match(/^properties\[(.+)\]$/);
        if (m && v !== '') props[m[1]] = v;
      }
      if (Object.keys(props).length) item.properties = props;
      if (fd.get('selling_plan')) item.selling_plan = fd.get('selling_plan');
      try {
        await SX.addToCart([item], this.form);
        this.setLoading(false);
        this.dispatchEvent(new CustomEvent('product-form:added', { bubbles: true }));
        SX.openCart(this.submitBtn);
      } catch (err) {
        this.setLoading(false);
        if (this.dataset.unsellableMessage && err.data && (err.data.status === 422 || err.data.status === '422')) err.message = this.dataset.unsellableMessage;
        if (this.errorEl) { this.errorEl.textContent = err.message; this.errorEl.hidden = false; }
        else SX.toast(err.message);
      }
    }
  }
  customElements.define('product-form', ProductForm);

  /* --------------------------------------------- quick add (product card) */
  class QuickAdd extends HTMLElement {
    connectedCallback() {
      this.openBtn = this.querySelector('[data-quick-open]');
      this.panel = this.querySelector('[data-quick-panel]');
      this.pick = this.querySelector('[data-quick-pick]');
      this.added = this.querySelector('[data-quick-added]');
      this.idInput = this.querySelector('[data-quick-variant]');
      this.submitBtn = this.querySelector('[data-quick-submit]');
      this.errorEl = this.querySelector('[data-quick-error]');
      const json = this.querySelector('[data-quick-variants]');
      this.variants = json ? JSON.parse(json.textContent) : null;
      this.selected = [];
      this.querySelectorAll('.sx-quick-panel__option').forEach((fs, i) => {
        const pressed = fs.querySelector('[aria-pressed="true"]');
        this.selected[i] = pressed ? pressed.dataset.value : null;
      });
      if (this.variants) {
        const first = this.variants.find((v) => v.available) || this.variants[0];
        if (first) this.selected = first.options.slice();
      }

      this.openBtn.addEventListener('click', (e) => { e.preventDefault(); this.variants ? this.openPanel() : this.addSingle(); });
      this.addEventListener('click', (e) => {
        const chip = e.target.closest('.sx-chip');
        if (chip && !chip.disabled) { this.choose(parseInt(chip.dataset.optionIndex, 10), chip.dataset.value); return; }
        if (e.target.closest('[data-quick-close]')) { this.closePanel(true); return; }
        if (e.target.closest('[data-quick-view-cart]') && SX.cartType !== 'page') { e.preventDefault(); this.closePanel(); SX.openCart(this.openBtn); }
      });
      this.panel.addEventListener('submit', (e) => { e.preventDefault(); this.submit(); });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !this.panel.hidden) { e.stopPropagation(); this.closePanel(true); } });
      this._outside = (e) => { if (!this.panel.hidden && !this.contains(e.target)) this.closePanel(); };
      document.addEventListener('click', this._outside);
      this.refresh();
    }
    disconnectedCallback() { document.removeEventListener('click', this._outside); }
    currentVariant() {
      if (!this.variants) return null;
      return this.variants.find((v) => v.options.every((o, i) => o === this.selected[i])) || null;
    }
    choose(index, value) {
      this.selected[index] = value;
      let v = this.currentVariant();
      if (!v || !v.available) {
        const alt = this.variants.find((x) => x.available && x.options[index] === value);
        if (alt) this.selected = alt.options.slice();
      }
      this.refresh();
    }
    refresh() {
      if (!this.variants) return;
      this.querySelectorAll('.sx-chip').forEach((chip) => {
        const i = parseInt(chip.dataset.optionIndex, 10);
        const val = chip.dataset.value;
        chip.setAttribute('aria-pressed', this.selected[i] === val ? 'true' : 'false');
        const anyAvailable = this.variants.some((v) => v.available && v.options[i] === val);
        chip.disabled = !anyAvailable;
        const withOthers = this.variants.some((v) => v.available && v.options.every((o, j) => (j === i ? o === val : o === this.selected[j])));
        chip.classList.toggle('is-unavailable', anyAvailable && !withOthers);
      });
      const v = this.currentVariant();
      if (v) this.idInput.value = v.id;
      const ok = v && v.available;
      this.submitBtn.disabled = !ok;
      this.submitBtn.querySelector('[data-quick-submit-label]').textContent = ok ? strings().addToCart : strings().soldOut;
    }
    openPanel() {
      this.panel.hidden = false;
      this.pick.hidden = false;
      this.added.hidden = true;
      this.openBtn.setAttribute('aria-expanded', 'true');
      this.closest('.sx-card') && this.closest('.sx-card').classList.add('is-hover');
      const first = this.panel.querySelector('.sx-chip[aria-pressed="true"]') || this.panel.querySelector('button');
      first && first.focus();
    }
    closePanel(returnFocus) {
      this.panel.hidden = true;
      this.openBtn.setAttribute('aria-expanded', 'false');
      this.closest('.sx-card') && this.closest('.sx-card').classList.remove('is-hover');
      if (returnFocus) this.openBtn.focus();
    }
    addSingle() {
      this.panel.hidden = false;
      this.pick.hidden = false;
      this.added.hidden = true;
      this.submit();
    }
    setLoading(on) {
      this.submitBtn.classList.toggle('is-loading', on);
      this.submitBtn.setAttribute('aria-busy', on ? 'true' : 'false');
      this.submitBtn.querySelector('.sx-spinner').hidden = !on;
      if (on) this.submitBtn.querySelector('[data-quick-submit-label]').textContent = strings().adding;
      this.openBtn.disabled = on;
    }
    async submit() {
      const id = parseInt(this.idInput.value, 10);
      if (!id) return;
      this.errorEl.hidden = true;
      this.setLoading(true);
      try {
        const data = await SX.addToCart([{ id, quantity: 1 }], this.panel);
        const item = (data.items && data.items[0]) || {};
        const variantTitle = item.variant_title && item.variant_title !== 'Default Title' ? ` · ${item.variant_title}` : '';
        this.querySelector('[data-quick-summary]').textContent = (strings().quickSummary || '[title] · [quantity]').replace('[title]', `${this.dataset.productTitle}${variantTitle}`).replace('[quantity]', item.quantity || 1);
        this.setLoading(false);
        this.pick.hidden = true;
        this.added.hidden = false;
        this.added.querySelector('a').focus({ preventScroll: true });
        if (SX.cartType !== 'page') {
          setTimeout(() => { this.closePanel(); SX.openCart(this.openBtn); }, 900);
        } else {
          setTimeout(() => this.closePanel(), 3500);
        }
      } catch (err) {
        this.setLoading(false);
        this.refresh();
        this.errorEl.textContent = err.message;
        this.errorEl.hidden = false;
      }
    }
  }
  customElements.define('quick-add', QuickAdd);

  /* --------------------------------------------------------- rail slider */
  class RailSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-rail]');
      if (!this.track) return;
      this.prev = this.querySelector('[data-rail-prev]');
      this.next = this.querySelector('[data-rail-next]');
      this.dots = this.querySelector('[data-rail-dots]');
      this.prev && this.prev.addEventListener('click', () => this.go(-1));
      this.next && this.next.addEventListener('click', () => this.go(1));
      this._onScroll = SX.debounce(() => this.update(), 60);
      this.track.addEventListener('scroll', this._onScroll, { passive: true });
      this._ro = new ResizeObserver(() => this.update());
      this._ro.observe(this.track);
      this.update();
    }
    disconnectedCallback() { this._ro && this._ro.disconnect(); }
    items() { return Array.from(this.track.children).filter((c) => c.offsetParent !== null); }
    step() {
      const items = this.items();
      if (items.length < 2) return this.track.clientWidth;
      return Math.abs(items[1].offsetLeft - items[0].offsetLeft) || items[0].clientWidth;
    }
    go(dir) {
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      this.track.scrollBy({ left: dir * this.step() * (rtl ? -1 : 1), behavior: reducedMotion() ? 'auto' : 'smooth' });
    }
    update() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const pos = Math.abs(this.track.scrollLeft);
      if (this.prev) this.prev.disabled = pos <= 2;
      if (this.next) this.next.disabled = pos >= max - 2;
      if (this.dots) {
        const items = this.items();
        const step = this.step() || 1;
        const visible = Math.max(1, Math.round(this.track.clientWidth / step));
        const pages = Math.max(1, items.length - visible + 1);
        if (this.dots.children.length !== pages) {
          this.dots.innerHTML = '';
          for (let i = 0; i < pages; i++) this.dots.appendChild(document.createElement('span'));
        }
        const idx = Math.min(pages - 1, Math.round(pos / step));
        Array.from(this.dots.children).forEach((d, i) => d.classList.toggle('is-active', i === idx));
        this.dots.hidden = pages < 2;
      }
    }
    onBlockSelect(block) {
      const item = block.closest('[data-rail] > *') || block;
      if (item && this.track.contains(item)) this.track.scrollTo({ left: item.offsetLeft - this.track.offsetLeft, behavior: 'auto' });
    }
  }
  customElements.define('rail-slider', RailSlider);

  /* ----------------------------------------------------- announcement bar */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.sx-announce__track');
      this.msgs = Array.from(this.querySelectorAll('.sx-announce__msg:not([aria-hidden="true"])'));
      this.bar = this.querySelector('.sx-announce');
      this.index = 0;
      this.speed = (parseInt(this.dataset.speed, 10) || 4) * 1000;
      if (this.msgs.length < 2 || !this.track) return;
      this.querySelector('[data-announce-prev]') && this.querySelector('[data-announce-prev]').addEventListener('click', () => { this.takeOver(); this.show(this.index - 1); });
      this.querySelector('[data-announce-next]') && this.querySelector('[data-announce-next]').addEventListener('click', () => { this.takeOver(); this.show(this.index + 1); });
      this.addEventListener('mouseenter', () => this.pause(true));
      this.addEventListener('mouseleave', () => this.pause(false));
      this.addEventListener('focusin', () => this.pause(true));
      this.addEventListener('focusout', () => this.pause(false));
    }
    disconnectedCallback() { clearInterval(this._timer); }
    takeOver() {
      if (this._js) return;
      this._js = true;
      this.bar.classList.add('is-js');
      this.restart();
    }
    restart() {
      clearInterval(this._timer);
      if (reducedMotion() || this._paused || this.dataset.autoplay === 'false') return;
      this._timer = setInterval(() => this.show(this.index + 1), this.speed);
    }
    pause(on) {
      this._paused = on;
      this.bar.classList.toggle('is-paused', on);
      if (this._js) { if (on) clearInterval(this._timer); else this.restart(); }
    }
    show(i) {
      const n = this.msgs.length;
      this.index = (i + n) % n;
      this.track.style.transform = `translateY(calc(var(--sx-announce-h) * -${this.index}))`;
      this.msgs.forEach((m, k) => m.setAttribute('aria-hidden', k === this.index ? 'false' : 'true'));
      this.restart();
    }
    onBlockSelect(block) {
      const i = this.msgs.indexOf(block);
      if (i > -1) { this.takeOver(); this.pause(true); this.show(i); }
    }
    onBlockDeselect() { this.pause(false); }
  }
  customElements.define('announcement-bar', AnnouncementBar);

  /* ------------------------------------------- header dropdowns (details) */
  class HeaderMenu extends HTMLElement {
    connectedCallback() {
      this.details = Array.from(this.querySelectorAll('details'));
      this.details.forEach((d) => {
        d.addEventListener('toggle', () => {
          if (d.open) this.details.forEach((o) => { if (o !== d) o.open = false; });
          const s = d.querySelector('summary');
          s && s.setAttribute('aria-expanded', d.open ? 'true' : 'false');
        });
        d.addEventListener('keydown', (e) => { if (e.key === 'Escape' && d.open) { d.open = false; d.querySelector('summary').focus(); } });
        d.addEventListener('focusout', (e) => { if (d.open && !d.contains(e.relatedTarget) && e.relatedTarget) d.open = false; });
        if (this.dataset.hover === 'true') {
          d.addEventListener('mouseenter', () => { if (window.matchMedia('(hover: hover)').matches) d.open = true; });
          d.addEventListener('mouseleave', () => { if (window.matchMedia('(hover: hover)').matches) d.open = false; });
        }
      });
      this._outside = (e) => this.details.forEach((d) => { if (d.open && !d.contains(e.target)) d.open = false; });
      document.addEventListener('click', this._outside);
    }
    disconnectedCallback() { document.removeEventListener('click', this._outside); }
  }
  customElements.define('header-menu', HeaderMenu);

  /* Sticky header height → CSS var (for sticky product columns etc.) */
  const setHeaderHeight = () => {
    const h = document.querySelector('.sx-header');
    if (h) document.documentElement.style.setProperty('--sx-sticky-offset', `${h.getBoundingClientRect().height + 16}px`);
  };
  window.addEventListener('resize', SX.debounce(setHeaderHeight, 100));
  document.addEventListener('DOMContentLoaded', setHeaderHeight);
  document.addEventListener('shopify:section:load', setHeaderHeight);

  /* ---------------------------------------------------------- search panel */
  class SearchPanel extends HTMLElement {
    connectedCallback() {
      this.sheet = this.querySelector('.sx-search-panel__sheet');
      this.input = this.querySelector('input[type="search"]');
      this._opener = (e) => {
        const btn = e.target.closest(`[aria-controls="${this.id}"]`);
        if (btn) { e.preventDefault(); this.open(btn); }
      };
      document.addEventListener('click', this._opener);
      this.addEventListener('click', (e) => { if (e.target.closest('[data-search-close]')) this.close(); });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
    }
    disconnectedCallback() { document.removeEventListener('click', this._opener); }
    open(trigger) {
      this.trigger = trigger;
      this.classList.add('is-open');
      this.removeAttribute('aria-hidden');
      this.sheet.removeAttribute('inert');
      trigger && trigger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('sx-lock');
      SX.trapFocus(this.sheet, this.input);
    }
    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      this.sheet.setAttribute('inert', '');
      this.trigger && this.trigger.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('sx-lock');
      SX.removeTrapFocus(this.trigger);
    }
  }
  customElements.define('search-panel', SearchPanel);

  /* ---------------------------------------------------------------- modal */
  class SxModal extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('.sx-modal__dialog');
      this._opener = (e) => {
        const btn = e.target.closest(`[aria-controls="${this.id}"]`);
        if (btn) { e.preventDefault(); this.open(btn); }
      };
      document.addEventListener('click', this._opener);
      this.addEventListener('click', (e) => { if (e.target.closest('[data-modal-close]')) this.close(); });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
    }
    disconnectedCallback() { document.removeEventListener('click', this._opener); }
    open(trigger) {
      this.trigger = trigger;
      this.classList.add('is-open');
      this.removeAttribute('aria-hidden');
      document.body.classList.add('sx-lock');
      SX.trapFocus(this.dialog);
    }
    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('sx-lock');
      SX.removeTrapFocus(this.trigger);
    }
  }
  customElements.define('sx-modal', SxModal);

  /* ---------------------------------------------------------- share links */
  class ShareLinks extends HTMLElement {
    connectedCallback() {
      const btn = this.querySelector('[data-copy-link]');
      if (!btn) return;
      btn.addEventListener('click', async () => {
        const url = this.dataset.url || window.location.href;
        try { await navigator.clipboard.writeText(url); }
        catch (e) {
          const t = document.createElement('textarea'); t.value = url; document.body.appendChild(t); t.select();
          try { document.execCommand('copy'); } catch (_) { /* ignore */ }
          t.remove();
        }
        SX.toast(strings().linkCopied, 2500);
        const status = this.querySelector('[data-copy-status]');
        if (status) status.textContent = strings().linkCopied;
      });
    }
  }
  customElements.define('share-links', ShareLinks);

  /* -------------------------------------------- cart upsell (recommendations) */
  class CartUpsell extends HTMLElement {
    connectedCallback() { this.load(); }
    async load() {
      const productId = this.dataset.productId;
      const sectionId = this.dataset.sectionId || 'cart-upsell';
      const exclude = (this.dataset.exclude || '').split(',').filter(Boolean);
      if (!productId) { this.reveal(); return; }
      for (const intent of ['complementary', 'related']) {
        try {
          const url = `${routes().product_recommendations}?product_id=${productId}&limit=6&section_id=${sectionId}&intent=${intent}`;
          const res = await fetch(url);
          if (!res.ok) continue;
          const html = await res.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const items = Array.from(doc.querySelectorAll('[data-upsell-item]')).filter((el) => !exclude.includes(el.dataset.productId));
          if (items.length) {
            const slot = this.querySelector('[data-upsell-slot]');
            slot.innerHTML = '';
            slot.appendChild(items[0]);
            this.reveal();
            return;
          }
        } catch (e) { /* try next */ }
      }
      this.reveal(true);
    }
    reveal(fallbackOnly) {
      const slot = this.querySelector('[data-upsell-slot]');
      const fallback = this.querySelector('template[data-upsell-fallback]');
      if (fallbackOnly && fallback && slot && !slot.children.length) slot.appendChild(fallback.content.cloneNode(true));
      this.hidden = !slot || !slot.children.length;
    }
  }
  customElements.define('cart-upsell', CartUpsell);

  /* Upsell "Add" buttons and any [data-add-variant] button add straight to cart */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-add-variant]');
    if (!btn) return;
    e.preventDefault();
    if (btn.classList.contains('is-loading')) return;
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
    try {
      await SX.addToCart([{ id: parseInt(btn.dataset.addVariant, 10), quantity: 1 }], btn);
      SX.openCart(btn);
    } catch (err) {
      SX.toast(err.message);
    } finally {
      btn.classList.remove('is-loading');
      btn.setAttribute('aria-busy', 'false');
    }
  });

  /* ------------------------------------------- spotlight video play/pause */
  class SpotlightMedia extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.btn = this.querySelector('[data-video-toggle]');
      if (!this.video || !this.btn) return;
      this.video.removeAttribute('controls');
      this.video.muted = true;
      this.btn.addEventListener('click', () => (this.video.paused ? this.play() : this.pause()));
      if (this.dataset.autoplay === 'true' && !reducedMotion()) this.play();
    }
    play() {
      const p = this.video.play();
      if (p && p.catch) p.catch(() => {});
      this.btn.classList.add('is-playing');
      this.btn.setAttribute('aria-label', this.btn.dataset.labelPause);
      this.btn.querySelector('use').setAttribute('href', '#i-pause');
    }
    pause() {
      this.video.pause();
      this.btn.classList.remove('is-playing');
      this.btn.setAttribute('aria-label', this.btn.dataset.labelPlay);
      this.btn.querySelector('use').setAttribute('href', '#i-play');
    }
  }
  customElements.define('spotlight-media', SpotlightMedia);

  /* ------------------------------------------- spotlight variant pills */
  /* Same pill picker as the product page: picks the variant from the options, then updates the hidden id,
     price block (prerendered per variant), button state (sold out only when inventory is really 0) and image. */
  class SpotlightPicker extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('[data-spotlight-variants]');
      if (!data) return;
      try { this.variants = JSON.parse(data.textContent); } catch (e) { return; }
      this.section = this.closest('section') || document;
      this.addEventListener('change', (e) => { if (e.target.matches('[data-option-index]')) this.update(); });
      this.mark();
    }
    selected() {
      return Array.from(this.querySelectorAll('fieldset.sx-option')).map((fs) => { const c = fs.querySelector('input:checked'); return c ? c.value : null; });
    }
    mark() {
      const opts = this.selected();
      this.querySelectorAll('input[data-option-index]').forEach((input) => {
        const i = parseInt(input.dataset.optionIndex, 10);
        const ok = this.variants.some((v) => !v.sold_out && v.options[i] === input.value && v.options.every((o, j) => j === i || o === opts[j]));
        input.classList.toggle('is-unavailable', !ok);
      });
      this.querySelectorAll('fieldset.sx-option').forEach((fs) => {
        const label = fs.querySelector('[data-selected-label]');
        const checked = fs.querySelector('input:checked');
        if (label && checked) label.textContent = checked.value;
      });
    }
    update() {
      this.mark();
      const opts = this.selected();
      const v = this.variants.find((x) => x.options.every((o, i) => o === opts[i]));
      const btn = this.querySelector('[data-add-button]');
      const label = btn && btn.querySelector('[data-label]');
      const ok = !!(v && !v.sold_out);
      if (btn) { btn.disabled = !ok; btn.setAttribute('aria-disabled', ok ? 'false' : 'true'); }
      if (label) label.textContent = !v ? strings().unavailable : ok ? strings().addToCart : strings().soldOut;
      if (!v) return;
      const input = this.querySelector('[data-spotlight-variant]');
      if (input) input.value = v.id;
      const price = this.section.querySelector('[data-spotlight-price]');
      if (price && v.price_html) price.innerHTML = v.price_html;
      if (v.image) {
        const img = this.section.querySelector('[data-spotlight-image]');
        if (img) { img.removeAttribute('srcset'); img.src = v.image; }
      }
    }
  }
  customElements.define('spotlight-picker', SpotlightPicker);

  /* ------------------------------- simple variant <select> (spotlight etc.) */
  class VariantSelect extends HTMLElement {
    connectedCallback() {
      this.select = this.querySelector('select');
      if (!this.select) return;
      this.select.addEventListener('change', () => this.update());
    }
    update() {
      const opt = this.select.selectedOptions[0];
      const target = document.getElementById(this.dataset.priceTarget);
      if (target && opt.dataset.priceHtml) target.innerHTML = opt.dataset.priceHtml;
      const form = this.closest('form');
      const btn = form && form.querySelector('[data-add-button]');
      if (btn) {
        const ok = !opt.hasAttribute('data-unavailable');
        btn.disabled = !ok;
        btn.setAttribute('aria-disabled', ok ? 'false' : 'true');
        const label = btn.querySelector('[data-label]');
        if (label) label.textContent = ok ? strings().addToCart : strings().soldOut;
      }
    }
  }
  customElements.define('variant-select', VariantSelect);

  /* ------------------------------------- deferred video (loads on play) */
  class DeferredVideo extends HTMLElement {
    connectedCallback() {
      this.btn = this.querySelector('[data-video-play]');
      if (!this.btn) return;
      this.btn.addEventListener('click', () => this.load(true));
      if (this.dataset.autoplay === 'true' && !reducedMotion()) this.load(false);
    }
    load(focus) {
      const tpl = this.querySelector('template');
      if (!tpl || this.loaded) return;
      this.loaded = true;
      const node = tpl.content.firstElementChild.cloneNode(true);
      this.appendChild(node);
      this.btn.remove();
      if (node.tagName === 'VIDEO') { const p = node.play(); if (p && p.catch) p.catch(() => {}); }
      if (focus) node.focus && node.focus();
    }
  }
  customElements.define('deferred-video', DeferredVideo);

  /* ----------------------------- countdown to a real date (never resets) */
  class CountdownTimer extends HTMLElement {
    connectedCallback() {
      this.end = parseInt(this.dataset.end, 10) * 1000;
      this.els = { d: this.querySelector('[data-days]'), h: this.querySelector('[data-hours]'), m: this.querySelector('[data-minutes]'), s: this.querySelector('[data-seconds]') };
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    tick() {
      const left = Math.max(0, this.end - Date.now());
      if (!left) {
        clearInterval(this.timer);
        const sec = document.getElementById(this.dataset.hideSection);
        if (sec && !SX.designMode) sec.hidden = true;
        return;
      }
      const t = Math.floor(left / 1000);
      const pad = (n) => String(n).padStart(2, '0');
      this.els.d.textContent = pad(Math.floor(t / 86400));
      this.els.h.textContent = pad(Math.floor((t % 86400) / 3600));
      this.els.m.textContent = pad(Math.floor((t % 3600) / 60));
      this.els.s.textContent = pad(t % 60);
    }
  }
  customElements.define('countdown-timer', CountdownTimer);

  /* ------------------------------- tracking-number form (track order page) */
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-track-form]');
    if (!form) return;
    e.preventDefault();
    const input = form.querySelector('input[name="tracking"]');
    const value = input && input.value.trim();
    if (!value) { input && input.focus(); return; }
    const url = form.dataset.pattern.replace('{number}', encodeURIComponent(value));
    window.open(url, '_blank', 'noopener');
  });

  /* --------------------- order lookup form (track page): validate, then open account orders */
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-order-lookup]');
    if (!form) return;
    e.preventDefault();
    const order = form.querySelector('[name="order"]');
    const email = form.querySelector('[name="email"]');
    const err = form.querySelector('[data-lookup-error]');
    const status = form.querySelector('[data-lookup-status]');
    const okEmail = !!email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim());
    const okOrder = !!order && order.value.trim().length > 0;
    [order, email].forEach((f) => f && f.removeAttribute('aria-invalid'));
    if (!okOrder || !okEmail) {
      if (!okOrder && order) order.setAttribute('aria-invalid', 'true');
      if (!okEmail && email) email.setAttribute('aria-invalid', 'true');
      if (err) err.hidden = false;
      (!okOrder ? order : email).focus();
      return;
    }
    if (err) err.hidden = true;
    if (status) {
      status.textContent = (strings().lookupGo || '').replace('[order]', order.value.trim());
      status.hidden = false;
    }
    setTimeout(() => { window.location.href = form.dataset.accountUrl; }, 900);
  });

  /* --------------------------------------------------------- page loader */
  const loader = () => document.querySelector('[data-page-loader]');
  let loaderTimer = null;
  const hideLoader = () => { clearTimeout(loaderTimer); const l = loader(); if (l) l.hidden = true; };
  document.addEventListener('click', (e) => {
    const l = loader();
    if (!l || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = e.target.closest('a[href]');
    if (!a || a.hasAttribute('download') || a.hasAttribute('data-no-loader')) return;
    if (a.target && a.target !== '_self') return;
    const href = a.getAttribute('href');
    if (!href || href.startsWith('#') || href.startsWith('javascript:')) return;
    let url;
    try { url = new URL(a.href, window.location.href); } catch (_) { return; }
    if (url.origin !== window.location.origin) return;
    if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) return;
    if (/\/cart\/(add|change|update|clear)/.test(url.pathname)) return;
    clearTimeout(loaderTimer);
    loaderTimer = setTimeout(() => { l.hidden = false; }, SX.loaderDelay || 300);
  });
  window.addEventListener('pageshow', hideLoader);
  window.addEventListener('pagehide', () => clearTimeout(loaderTimer));

  /* ----------------------------------------------------- reveal on scroll */
  let io = null;
  function initReveal(scope) {
    if (!document.body.classList.contains('sx-anim') || reducedMotion()) return;
    if (!('IntersectionObserver' in window)) { (scope || document).querySelectorAll('.sx-reveal').forEach((el) => el.classList.add('is-visible')); return; }
    io = io || new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-visible'); io.unobserve(en.target); } });
    }, { rootMargin: '0px 0px -8% 0px' });
    (scope || document).querySelectorAll('.sx-reveal:not(.is-visible)').forEach((el) => io.observe(el));
  }
  document.addEventListener('DOMContentLoaded', () => initReveal());
  SX.initReveal = initReveal;
})();
